import './App.css'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { AuthProvider } from '@/Context/AuthContext'
import { ToastContainer } from 'react-toastify'
import AuthLayout from '@/Components/AuthLayout'
import ErrorBoundary from './ErrorBoundary'
import React, { Suspense, lazy, useEffect } from 'react'
import PermissionCheck from './Components/PermissionCheck'
import CommonApiService from './Service/CommonApiService'
import Mitem from './Pages/Setup/Mitem/Listing/Mitem'
import CreateMitem from './Pages/Setup/Mitem/CreateMitem'
import UpdateMitem from './Pages/Setup/Mitem/UpdateMitem'
import PayorTypeService from './Service/PayorTypeService'
import MitemPayorTypeService from './Service/MitemPayorTypeService'
import HCHBPayorTypeServce from './Service/HCHBPayorTypeServce'
import WmsSettings from './Pages/AccessControl/WmsSettings/WmsSettings'
import WMSSettingsService from './Service/WMSSettingsService'
import SectionService from './Service/SectionService'
import ClientService from './Service/ClientService'
import MitemService from './Service/MitemService'
import UserListing from './Pages/User/Listing/UserListing'
import CreateUser from './Pages/User/CreateUser'
import UpdateUser from './Pages/User/UpdateUser'
import UserService from './Service/UserService'
import AssessmentTypeService from './Service/AssessmentTypeService'
import WorkAllocation from './Pages/WorkAllocation/WorkAllocation'
import GroupConfigurationListing from './Pages/Configuration/GroupConfiguration/Listing/GroupConfigurationListing'
import CreateGroupConfiguration from './Pages/Configuration/GroupConfiguration/CreateGroupConfiguration'
import BulkUpload from './Pages/BulkUpload/Listing/BulkUpload'
import CreateBulkUpload from './Pages/BulkUpload/CreateBulkUpload'

import BulkUploadService from './Service/BulkUploadService'
import UpdateBulkUpload from './Pages/BulkUpload/UpdateBulkUpload'
import ActivityLogService from './Service/ActivityLogService'
import ActivityLogListing from './Pages/ActivityLog/Listing/ActivityLogListing'
import ViewActivityLog from './Pages/ActivityLog/ViewActivityLog'
import UpdateGroupConfigurations from './Pages/Configuration/GroupConfiguration/UpdateGroupConfigurations'
import GroupConfigurationService from './Service/GroupConfigurationService'
import WorkflowAction from './Pages/WorkFlow/WorkflowAction/Listing/WorkflowAction'
import WorkflowState from './Pages/WorkFlow/WorkflowState/Listing/WorkflowState'
import WorkFlowListing from './Pages/WorkFlow/WorkAllocation/Listing/WorkFlowListing'
import CreateWorkflow from './Pages/WorkFlow/WorkAllocation/CreateWorkflow'
import WorkflowStateService from './Service/WorkflowStateService'
import WorkflowActionService from './Service/WorkflowActionService'
import EditWorkflow from './Pages/WorkFlow/WorkAllocation/EditWorkflow'
import WorkflowService from './Service/WorkflowService'
import FollowupStatus from './Pages/Setup/FollowupStatus/Listing/FollowupStatus'
import FollowupStatusService from './Service/FollowupStatusService'
import CreateWorkAllocation from './Pages/WorkAllocation/CreateWorkAllocation'
import WorkAllocationForm from './Pages/WorkAllocation/WorkAllocationForm'
import WorkAllocationService from './Service/WorkAllocationService'
import ChartLockIssue from './Pages/Setup/ChartLockIssue/Lising/ChartLockIssue'
import ChartLockIssueService from './Service/ChartLockIssueService'
import HoldReasonService from './Service/HoldReasonService'
import TechnicalissueService from './Service/TechnicalissueService'
import WorkAllocationFormService from './Service/WorkAllocationFormService'
import ShiftType from './Pages/Setup/ShiftType/Listing/ShiftType'
import CreateShiftType from './Pages/Setup/ShiftType/CreateShiftType'
import UpdateShiftType from './Pages/Setup/ShiftType/UpdateShiftType'
import ShiftTypeService from './Service/ShiftTypeService'

//Lazy-loaded components
const Login2 = lazy(() => import('@/Pages/Login2'))
const Dashboard = lazy(() => import('@/Pages/Dashboard/Dashboard'))
const PayorTypeList = lazy(() => import('@/Pages/Setup/PayorType/Listing/PayorTypeList'))
const PagePermissions = lazy(() => import('@/Pages/AccessControl/PagePermissions/PagePermissions'))
const NotFound404 = lazy(() => import('./Pages/Error/NotFound404'))
const ErrorPage = lazy(() => import('./ErrorPage'))
const MitemPayorType = lazy(() => import('./Pages/Setup/MitemPayorType/Listing/MitemPayorType'))
const TechnicalIssue = lazy(() => import('./Pages/Setup/TechnicalIssue/Lisitng/TechnicalIssue'))
const HoldReason = lazy(() => import('./Pages/Setup/HoldReason/Listing/HoldReason'))
const AssessmentType = lazy(() => import('./Pages/Setup/AssessmentType/Lising/AssessmentType'))
const HCHBPayorType = lazy(() => import('./Pages/Setup/HCHBPayorType/Listing/HCHBPayorType'))
const CreateHCHBPayorType = lazy(() => import('./Pages/Setup/HCHBPayorType/CreateHCHBPayorType'))
const Client = lazy(() => import('./Pages/Client/Listing/Client'))
const CreateClient = lazy(() => import('./Pages/Client/CreateClient'))
const UpdateClient = lazy(() => import('./Pages/Client/UpdateClient'))
const UpdateSection = lazy(() => import('./Pages/Setup/Section/UpdateSection'))
const Section = lazy(() => import('./Pages/Setup/Section/Listing/Section'))
const CreateSection = lazy(() => import('./Pages/Setup/Section/CreateSection'))
const UpdateHCHBPayorType = lazy(() => import('./Pages/Setup/HCHBPayorType/UpdateHCHBPayorType'))

const requiredRoles = ['System Manager', 'Medical Coder']
function AppRouter() {
    // const userRoles = useSelector((state) => state.auth.user?.roles || []) // Adjust path based on your store

    return <RouterProvider router={router} />
}

const router = createBrowserRouter([
    {
        path: '/login',
        element: <Login2 />,
    },
    {
        path: '/',
        element: <AuthLayout />,
        children: [
            {
                index: true,
                element: (
                    <PermissionCheck requiredRoles={requiredRoles}>
                        <Dashboard />
                    </PermissionCheck>
                ),
            },
            {
                path: '/bulk-upload',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <BulkUpload />
                    </PermissionCheck>
                ),
                loader: async () => {
                    try {
                        const bulkUpload = await BulkUploadService.getBulkUploadRecords()
                        return { bulkUpload: bulkUpload?.data?.data || [] }
                    } catch {}
                    return { bulkUpload: [] }
                },
            },
            {
                path: '/bulk-upload/new',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <CreateBulkUpload />
                    </PermissionCheck>
                ),
                loader: async () => {
                    try {
                        const roles = await WMSSettingsService.getWmsRoles()
                        return { roles: roles?.data?.data || [] }
                    } catch {}
                },
            },
            {
                path: '/bulk-upload/:name',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <UpdateBulkUpload />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    try {
                        const bulkUpload = await BulkUploadService.getBulkUploadRecord(params.name)
                        return { bulkUpload: bulkUpload?.data?.data || null }
                    } catch (e) {}
                    return { bulkUpload: null }
                },
            },
            {
                path: '/activity-log',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <ActivityLogListing />
                    </PermissionCheck>
                ),
            },
            {
                path: '/activity-log/:name',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <ViewActivityLog />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    try {
                        const activityLog = await ActivityLogService.getActivityLog(params.name)
                        return { activityLog: activityLog?.data?.data || null }
                    } catch (e) {}
                    return { activityLog: null }
                },
            },
            {
                path: '/work-allocation',
                element: (
                    <PermissionCheck requiredRoles={['System Manager', 'Medical Coder']}>
                        <WorkAllocation />
                    </PermissionCheck>
                ),
                loader: async () => {
                    const res_assessment_type = await AssessmentTypeService.getAssessmentTypes()
                    const res_payor_type = await PayorTypeService.getPayorTypes()
                    const res_followup_status = await FollowupStatusService.getFollowupStatus()
                    const res_chart_status = await WorkAllocationService.getChartStatus('')
                    const res_char_lock_issue = await ChartLockIssueService.getChartLockIssue()
                    const res_coder_name = await UserService.getUsersByRole({ role: 'Medical Coder' })
                    const res_qa_name = await UserService.getUsersByRole({ role: 'QA' })
                    const res_qa_tl_name = await UserService.getUsersByRole({ role: 'QA TL' })
                    const res_prd_tl_name = await UserService.getUsersByRole({ role: 'Production TL' })

                    let chart_status1 = res_chart_status.data.data
                    const chartStatus = chart_status1.map((item) => ({ chart_status: item }))
                    let assessment_type = res_assessment_type.data.data
                    let payor_type = res_payor_type.data.data
                    let followupStatus = res_followup_status.data.data
                    let chartLockIssue = res_char_lock_issue.data.data
                    let coder = res_coder_name.data.data
                    let qa = res_qa_name.data.data
                    let qa_tl = res_qa_tl_name.data.data
                    let prod_tl = res_prd_tl_name.data.data
                    return { payor_type, assessment_type, followupStatus, chartStatus, chartLockIssue, coder, qa, qa_tl, prod_tl }
                },
            },
            {
                path: '/work-allocation/create-work-allocation',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <CreateWorkAllocation />
                    </PermissionCheck>
                ),
                loader: async () => {
                    const res_assessment_type = await AssessmentTypeService.getAssessmentTypes()
                    const res_payor_type = await PayorTypeService.getPayorTypes()
                    let assessmentType = res_assessment_type.data.data
                    let payorType = res_payor_type.data.data
                    return { payorType, assessmentType }
                },
            },
            {
                path: '/work-allocation/:id',
                element: (
                    <PermissionCheck requiredRoles={['System Manager', 'Medical Coder']}>
                        {console.log('ds')}
                        <WorkAllocationForm />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    console.log('loader start')
                    try {
                        const res_char_lock_issue = await ChartLockIssueService.getChartLockIssue()
                        let chartLockIssue = res_char_lock_issue.data.data
                        const res_hold_reson = await HoldReasonService.getHoldReason()
                        let holdReason = res_hold_reson.data.data
                        const res_technical_issue = await TechnicalissueService.getTechnicalissue()
                        let technicalIssue = res_technical_issue.data.data
                        const res_category = await WorkAllocationFormService.getCategory(params.id, '')
                        const res_sub_category = await WorkAllocationFormService.getSubCategory()
                        const res_followup_status = await WorkAllocationFormService.getFollowupStatus()
                        const followupStatus = res_followup_status.data.data
                        const subCategory = res_sub_category.data.data
                        let category = res_category.data.data
                        let res_workflow = await WorkflowService.getWorkflow('Work Allocation')
                        let workflowData = res_workflow.data.data.wms_workflow_table

                        console.log('Workflow data', workflowData)
                        return { chartLockIssue, holdReason, technicalIssue, category, subCategory, followupStatus, workflowData }
                    } catch (err) {
                        console.log('onload error', err)
                        return {
                            chartLockIssue: [],
                            holdReason: [],
                            technicalIssue: [],
                            category: [],
                            subCategory: [],
                            followupStatus: [],
                            workflowData: [],
                        }
                    }
                },
            },
            {
                path: '/user',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <UserListing />
                    </PermissionCheck>
                ),
            },
            {
                path: '/user/new',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <CreateUser />
                    </PermissionCheck>
                ),
            },
            {
                path: '/user/:email',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <UpdateUser />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    const user = await UserService.getUser(params.email)
                    let userData = user.data.data
                    const resp = await WMSSettingsService.getWmsRoles()
                    let roleData = resp.data.data
                    return { userData, roleData }
                },
            },
            {
                path: '/work-allocation',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <WorkAllocation />
                    </PermissionCheck>
                ),
                loader: async () => {
                    const res_assessment_type = await AssessmentTypeService.getAssessmentTypes()
                    const res_payor_type = await PayorTypeService.getPayorTypes()
                    let assessment_type = res_assessment_type.data.data
                    let payor_type = res_payor_type.data.data
                    return { payor_type, assessment_type }
                },
            },
            {
                path: '/workflow_',
                element: (
                    <PermissionCheck requiredRoles={requiredRoles}>
                        <WorkFlowListing />
                    </PermissionCheck>
                ),
            },
            {
                path: 'workflow/create-workflow',
                element: (
                    <PermissionCheck requiredRoles={requiredRoles}>
                        <CreateWorkflow />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    const state = await WorkflowStateService.getWorkflowState()
                    let stateData = state.data.data
                    const action = await WorkflowActionService.getWorkflowAction()
                    let actionData = action.data.data
                    const resp = await WMSSettingsService.getWmsRoles()
                    let roleData = resp.data.data
                    return { stateData, roleData, actionData }
                },
            },
            {
                path: 'workflow/:name',
                element: (
                    <PermissionCheck requiredRoles={requiredRoles}>
                        <EditWorkflow />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    const workflow = await WorkflowService.getWorkflow(params.name)
                    console.log('workflow', workflow)
                    const workflowData = workflow.data.data
                    const state = await WorkflowStateService.getWorkflowState()
                    let stateData = state.data.data
                    const action = await WorkflowActionService.getWorkflowAction()
                    let actionData = action.data.data
                    const resp = await WMSSettingsService.getWmsRoles()
                    let roleData = resp.data.data
                    return { stateData, roleData, actionData, workflowData }
                },
            },
            {
                path: 'workflow/action',
                element: (
                    <PermissionCheck requiredRoles={requiredRoles}>
                        <WorkflowAction />
                    </PermissionCheck>
                ),
            },
            {
                path: 'workflow/state',
                element: (
                    <PermissionCheck requiredRoles={requiredRoles}>
                        <WorkflowState />
                    </PermissionCheck>
                ),
            },
            {
                path: '/client',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <Client />
                    </PermissionCheck>
                ),
            },

            {
                path: '/client/new',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <CreateClient />
                    </PermissionCheck>
                ),
            },
            {
                path: '/client/:client',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <UpdateClient />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    const client = await ClientService.getClient(params.client)
                    let clientData = client.data.data
                    return { clientData }
                },
            },
            {
                path: 'setup/payor-type',
                element: (
                    <PermissionCheck requiredRoles={requiredRoles}>
                        <PayorTypeList />
                    </PermissionCheck>
                ),
            },
            {
                path: 'access-control/permissions',
                element: (
                    <PermissionCheck requiredRoles={requiredRoles}>
                        <PagePermissions />
                    </PermissionCheck>
                ),
                loader: async () => {
                    try {
                        const roles = await WMSSettingsService.getWmsRoles()
                        return { roles: roles?.data?.data || [] }
                    } catch {}
                },
            },
            {
                path: '/setup/assessment-type',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <AssessmentType />
                    </PermissionCheck>
                ),
            },
            {
                path: '/setup/hold-reason',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <HoldReason />
                    </PermissionCheck>
                ),
            },
            {
                path: '/setup/technical-issue',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <TechnicalIssue />
                    </PermissionCheck>
                ),
            },
            {
                path: '/setup/payor-type',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <PayorTypeList />
                    </PermissionCheck>
                ),
            },
            {
                path: '/setup/hchb-payor-type',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <HCHBPayorType />
                    </PermissionCheck>
                ),
            },
            {
                path: '/setup/hchb-payor-type/new',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <CreateHCHBPayorType />
                    </PermissionCheck>
                ),
                loader: async () => {
                    const res_payor = await PayorTypeService.getPayorTypes()
                    const res_mitem_payor = await MitemPayorTypeService.getMitemPayorTypes()
                    let payorType = res_payor.data.data
                    let mitemPayorType = res_mitem_payor.data.data
                    return { payorType, mitemPayorType }
                },
            },
            {
                path: '/setup/hchb-payor-type/:hchb_payor_type',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <UpdateHCHBPayorType />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    console.log('params', params)
                    const hchb_payor_type = await HCHBPayorTypeServce.getHCHBPayorType(params.hchb_payor_type)
                    const res_payor = await PayorTypeService.getPayorTypes()
                    const res_mitem_payor = await MitemPayorTypeService.getMitemPayorTypes()
                    let payorType = res_payor.data.data
                    let mitemPayorType = res_mitem_payor.data.data
                    let hchbPayorType = hchb_payor_type.data.data
                    return { payorType, mitemPayorType, hchbPayorType }
                },
            },
            {
                path: '/setup/mitem-payor-type',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <MitemPayorType />
                    </PermissionCheck>
                ),
            },

            {
                path: '/setup/section',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <Section />
                    </PermissionCheck>
                ),
            },
            {
                path: '/setup/section/new',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <CreateSection />
                    </PermissionCheck>
                ),
            },
            {
                path: '/setup/section/:section',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <UpdateSection />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    const section = await SectionService.getSection(params.section)
                    let sectionData = section.data.data

                    return { sectionData }
                },
            },

            {
                path: '/setup/shift-type',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <ShiftType />
                    </PermissionCheck>
                ),
            },
            {
                path: '/setup/shift-type/new',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <CreateShiftType />
                    </PermissionCheck>
                ),
            },
            {
                path: '/setup/shift-type/:shift_id',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <UpdateShiftType />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    const shift_type = await ShiftTypeService.getShiftType(params.shift_id)
                    let shiftData = shift_type.data.data

                    return { shiftData }
                },
            },
            {
                path: '/setup/mitem',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <Mitem />
                    </PermissionCheck>
                ),
            },
            {
                path: '/setup/mitem/new',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <CreateMitem />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    console.log('params', params)
                    const section = await SectionService.getSections()
                    let sectionData = section.data.data
                    const assessment_type = await AssessmentTypeService.getAssessmentTypes()
                    const res_mitem_payor = await MitemPayorTypeService.getMitemPayorTypes()
                    let assessmentType = assessment_type.data.data
                    let mitemPayorType = res_mitem_payor.data.data
                    return { sectionData, mitemPayorType, assessmentType }
                },
            },
            {
                path: '/setup/mitem/:mitem',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <UpdateMitem />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    const section = await SectionService.getSections()
                    let sectionData = section.data.data
                    const mitem = await MitemService.getMitem(params.mitem)
                    let mitemData = mitem.data.data
                    const assessment_type = await AssessmentTypeService.getAssessmentTypes()
                    const res_mitem_payor = await MitemPayorTypeService.getMitemPayorTypes()
                    let assessmentType = assessment_type.data.data
                    let mitemPayorType = res_mitem_payor.data.data
                    return { sectionData, mitemData, mitemPayorType, assessmentType }
                },
            },
            {
                path: '/setup/followup-status',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <FollowupStatus />
                    </PermissionCheck>
                ),
            },
            {
                path: '/setup/chart-lock-issue',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <ChartLockIssue />
                    </PermissionCheck>
                ),
            },
            {
                path: '/access-control/settings',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <WmsSettings />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    const wmsSettings = await WMSSettingsService.getWmsSettings()
                    const roles = await CommonApiService.getRoles()

                    return {
                        roles: roles.data.data || [],
                        wmsSettings: wmsSettings.data.data || {},
                    }
                },
            },
            {
                path: '/configuration/group-configuration',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <GroupConfigurationListing />
                    </PermissionCheck>
                ),
            },
            {
                path: '/configuration/group-configuration/new',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <CreateGroupConfiguration />
                    </PermissionCheck>
                ),
            },
            {
                path: '/configuration/group-configuration/:name',
                element: (
                    <PermissionCheck requiredRoles={['System Manager']}>
                        <UpdateGroupConfigurations />
                    </PermissionCheck>
                ),
                loader: async ({ params }) => {
                    try {
                        const results = await Promise.all([
                            GroupConfigurationService.getGroupConfigurationById(params.name),
                            MitemPayorTypeService.getMitemPayorTypes(),
                            AssessmentTypeService.getAssessmentTypes(),
                        ])
                        let postData = {
                            role: results[0].data.data.role,
                        }
                        console.log('post,dat : ', postData, results[0].data.data.role)
                        const resp = await UserService.getUsersByRole(postData)

                        return {
                            groupConfiguration: results[0]?.data?.data || null,
                            payorType: results[1]?.data?.data || [],
                            assessmentType: results[2]?.data?.data || [],
                            employee: resp.data.data,
                        }
                    } catch {}
                },
            },
            {
                path: '*',
                element: <NotFound404 />,
            },
        ],
    },
    {
        path: '*',
        element: <ErrorPage />,
    },
])

function App() {
    return (
        <AuthProvider>
            <ToastContainer
                position="top-center"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <RouterProvider router={router} />
        </AuthProvider>
    )
}

export default App
